import { useEffect, useState } from 'react'

import { Pencil, Trash2 } from 'lucide-react'

import {
  NoteAvatar,
  NoteNotice,
  NoteTimestamp,
  noteCardClass,
} from '@/components/CalloutNote'
import { NoteMarkdown } from '@/components/NoteMarkdown'
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor'
import { NotePhotos } from '@/components/ui/PhotoGallery'

export type CalloutPhotoNoteMode = 'read' | 'edit'

export interface CalloutPhotoNoteProps {
  authorName: string
  createdAt?: string
  photoUrl?: string
  /** Every photo the note currently shows; falls back to [photoUrl]. */
  photoUrls?: string[]
  /** Optional short description or caption for the photo group. */
  caption?: string
  /** Visual mode: read-only or replacing an existing photo. */
  mode?: CalloutPhotoNoteMode
  /** When false in read mode, hides the edit button. */
  showEditButton?: boolean
  /** When true in read mode, shows a delete button. */
  showDeleteButton?: boolean
  /** Optional callback when the edit icon is clicked in read mode. */
  onClickEdit?: () => void
  /** Optional callback when delete is clicked in read mode. */
  onClickDelete?: () => void
  /** Optional callback when cancel is clicked in edit mode. */
  onClickCancel?: () => void
  /**
   * Optional callback when "Replace photo" is clicked in edit mode.
   * In a real app this would trigger capture / file selection.
   */
  onClickReplacePhoto?: () => void
  /**
   * Optional callback when "Crop / rotate photo" is clicked in edit mode.
   * Opens the composer pre-loaded with the existing image.
   */
  onClickAdjustPhoto?: () => void
  /**
   * Optional callback when save is clicked in edit mode.
   * Receives the latest caption value.
   */
  onClickSave?: (nextCaption: string) => void
  /** When true in read mode, clicking the photo opens a fullscreen preview dialog. */
  enablePreview?: boolean
  /**
   * Seed for the caption editor when it should differ from `caption` — set
   * when a read-mode checkbox tap opened the editor with that box flipped.
   */
  initialDraftCaption?: string
  /**
   * Makes read-mode caption checkboxes tappable (see NoteMarkdown).
   * Absent = inert.
   */
  onToggleTask?: (taskIndex: number) => void
  /** Small transient message under the note (e.g. a permissions denial). */
  notice?: string | null
}

export function CalloutPhotoNote(props: CalloutPhotoNoteProps) {
  const {
    authorName,
    createdAt,
    photoUrl = '/1200x800.svg',
    photoUrls,
    caption,
    mode = 'read',
    showEditButton = true,
    showDeleteButton = false,
    onClickEdit,
    onClickDelete,
    onClickCancel,
    onClickReplacePhoto,
    onClickAdjustPhoto,
    onClickSave,
    initialDraftCaption,
    onToggleTask,
    notice,
  } = props

  const savedCaption = caption ?? ''
  const seedCaption = initialDraftCaption ?? savedCaption
  const [draftCaption, setDraftCaption] = useState(seedCaption)
  // Remounts the editor whenever the seed changes, so entering edit mode
  // (or entering it via a checkbox tap) loads the right document.
  const [editorSeed, setEditorSeed] = useState(seedCaption)

  // Each time we enter edit mode, prefill the caption input
  // with the latest caption from props.
  useEffect(() => {
    if (mode === 'edit') {
      setDraftCaption(seedCaption)
      setEditorSeed(seedCaption)
    }
  }, [mode, seedCaption])

  const groupUrls =
    photoUrls && photoUrls.length > 0 ? photoUrls : [photoUrl]
  const isGroup = groupUrls.length > 1

  // A photo note's evidence is its image, so a caption edit is not the only
  // thing worth saving — replacing the photo counts too. Only the caption is
  // draft state here, so that alone gates the button.
  const isDirty = draftCaption !== savedCaption


  if (mode === 'edit') {
    return (
      <div className={noteCardClass}>
        <NoteAvatar name={authorName} />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-slate-900">
              {authorName}
            </span>
            <span className="text-[10.5px] tracking-wide text-amber-700 uppercase">
              {isGroup ? 'Editing photo note' : 'Replacing photo'}
            </span>
            <span className="flex-1" />
            <NoteTimestamp createdAt={createdAt} />
          </div>
          {isGroup ? (
            <NotePhotos
              photoUrls={groupUrls}
              caption={caption}
              enablePreview={false}
            />
          ) : (
            <button
              type="button"
              className="relative flex w-full justify-center overflow-hidden rounded-md border border-[#e7e2d5] bg-slate-100"
              onClick={onClickReplacePhoto}
            >
              <img
                src={photoUrl}
                alt={caption || 'Work photo'}
                className="max-h-[200px] w-auto max-w-full object-contain"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center bg-slate-900/70 py-1 text-[11px] font-medium tracking-wide text-slate-50 uppercase">
                <span>Tap to replace photo</span>
              </div>
            </button>
          )}
          {onClickAdjustPhoto && !isGroup ? (
            <div className="flex justify-end text-[11px]">
              <button
                type="button"
                className="font-medium text-blue-600 hover:text-blue-700"
                onClick={onClickAdjustPhoto}
              >
                Crop / rotate photo
              </button>
            </div>
          ) : null}
          <div className="space-y-1">
            <span className="text-[11px] font-medium tracking-wide text-slate-500 uppercase">
              Caption <span className="normal-case">(optional)</span>
            </span>
            <RichMarkdownEditor
              key={editorSeed}
              initialValue={editorSeed}
              onChange={setDraftCaption}
              placeholder="Describe what this photo shows…"
              ariaLabel="Photo caption"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              className="inline-flex flex-1 items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
              onClick={onClickCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!isDirty}
              className={`inline-flex flex-1 items-center justify-center rounded-md px-3 py-2 text-xs font-medium ${
                isDirty
                  ? 'bg-slate-900 text-white hover:bg-slate-800'
                  : 'cursor-not-allowed bg-slate-200 text-slate-400'
              }`}
              onClick={() => {
                if (!onClickSave || !isDirty) return
                onClickSave(draftCaption)
              }}
            >
              {isDirty ? 'Save photo note' : 'Saved'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={noteCardClass}>
      <NoteAvatar name={authorName} />
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-900">
            {authorName}
          </span>
          <span className="text-[10.5px] text-amber-700">
            {isGroup ? `added ${groupUrls.length} photos` : 'added a photo'}
          </span>
          <span className="flex-1" />
          <NoteTimestamp createdAt={createdAt} />
          {showEditButton ? (
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-amber-100 hover:text-slate-600"
              onClick={onClickEdit}
              aria-label="Edit photo note"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
          {showDeleteButton ? (
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600"
              onClick={onClickDelete}
              aria-label="Delete note"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <NotePhotos
          photoUrls={groupUrls}
          caption={caption}
          enablePreview={Boolean(props.enablePreview)}
        />
        {caption ? (
          <NoteMarkdown
            text={caption}
            className="mt-1 text-[12.5px] leading-normal text-stone-900"
            onToggleTask={onToggleTask}
          />
        ) : null}
        {notice ? <NoteNotice message={notice} /> : null}
      </div>
    </div>
  )
}
